import React, {
  useCallback, useEffect, useRef, useState,
} from "react"
import types from "prop-types"
import { useMentionableMenuKeyboardNavigation, useTextareaCaretOffset } from "hooks"
import MentionableTextInputMenuItem from "./MentionableTextInputMenuItem"

const MentionableTextInputMenu = ({
  clearMentionables, insertMentionable, mentionables = [], textareaRef,
}) => {
  const menuRef = useRef()

  const { left, top } = useTextareaCaretOffset(textareaRef)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const incrementHighlightedIndex = useCallback(() => {
    setHighlightedIndex((currentHighlightedIndex) => {
      const maxHighlightedIndex = mentionables.length - 1

      const newHighlightedIndex = currentHighlightedIndex >= maxHighlightedIndex ? maxHighlightedIndex : currentHighlightedIndex + 1
      const indexToMakeVisible = newHighlightedIndex === maxHighlightedIndex ? newHighlightedIndex : newHighlightedIndex + 1

      if (menuRef.current) {
        menuRef.current.children[indexToMakeVisible].scrollIntoView({ behavior: "smooth", block: "nearest" })
      }

      return newHighlightedIndex
    })
  }, [mentionables])

  const decrementHighlightedIndex = useCallback(() => {
    setHighlightedIndex((currentHighlightedIndex) => {
      const newHighlightedIndex = currentHighlightedIndex <= 0 ? 0 : currentHighlightedIndex - 1
      const indexToMakeVisible = newHighlightedIndex === 0 ? newHighlightedIndex : newHighlightedIndex - 1

      if (menuRef.current) {
        menuRef.current.children[indexToMakeVisible].scrollIntoView({ behavior: "smooth", block: "nearest" })
      }

      return newHighlightedIndex
    })
  }, [])

  const mentionHighlighted = useCallback(() => {
    insertMentionable(highlightedIndex)
  }, [highlightedIndex, insertMentionable])

  useMentionableMenuKeyboardNavigation({
    clearMentionables, decrementHighlightedIndex, incrementHighlightedIndex, mentionHighlighted,
  })

  useEffect(() => {
    setHighlightedIndex(0)
  }, [mentionables])

  return (
    <div className="mention-menu" style={{ left: left + 24, top: top + 24 }}>
      <ul ref={menuRef} role="listbox">
        {
          mentionables.map((user, index) => (
            <MentionableTextInputMenuItem
              highlighted={highlightedIndex === index}
              key={user.id}
              label={user.fullName}
              onClick={() => insertMentionable(index)}
              onMouseEnter={() => setHighlightedIndex(index)}
            />
          ))
        }
      </ul>
    </div>
  )
}

MentionableTextInputMenu.propTypes = {
  clearMentionables: types.func.isRequired,
  insertMentionable: types.func.isRequired,
  mentionables: types.arrayOf(types.shape({
    fullName: types.string.isRequired,
    id: types.number.isRequired,
  })),
  textareaRef: types.oneOfType([
    types.func,
    types.shape({ current: types.instanceOf(Element) }),
  ]).isRequired,
}

export default MentionableTextInputMenu
